// React Compiler opt-out: the `table` prop is TanStack's mutable, stable-identity instance —
// getRowModel() results change under it without a new reference, so compiled caching would
// freeze rows on sort/pagination. The compiler's incompatible-library bailout only covers the
// useReactTable() call site in DataTable.tsx, not this child.
'use no memo';

import React from 'react';

import LoadingSpinnerLong from '../../../molecules/LoadingSpinnerLong/LoadingSpinnerLong.react';
import EmptyTable from '../EmptyTable/EmptyTable.react';

import DataTableRow from './DataTableRow';
import type {DataTableBodyProps} from './DataTable.types';

/**
 * Renders the `<tbody>` for DataTable.
 *
 * Handles four states:
 * 1. **Loading** – full-table spinner (initial fetch)
 * 2. **Reloading with prior rows** – tbody keeps the previous rows so the layout
 *    doesn't shift; DataTable renders a semi-transparent overlay spinner above.
 * 3. **Reloading with no prior rows** – suppress the empty-state so it doesn't
 *    appear behind the overlay spinner; a sized placeholder cell reserves height.
 * 4. **Data / Empty** – rows or empty-state fallback
 *
 * Uses the existing `data-table__*` CSS classes for visual parity.
 */
function DataTableBody<TData>({
  table,
  isLoading,
  isReloading,
  emptyState,
  columnCount
}: DataTableBodyProps<TData>) {
  if (isLoading) {
    return (
      <tbody>
        <tr>
          <td className='data-table__loading' colSpan={columnCount}>
            <LoadingSpinnerLong />
          </td>
        </tr>
      </tbody>
    );
  }

  const rows = table.getRowModel().rows;
  const isEmpty = rows.length === 0;

  if (isReloading && isEmpty) {
    return (
      <tbody>
        <tr>
          <td className='data-table__loading' colSpan={columnCount} aria-hidden='true' />
        </tr>
      </tbody>
    );
  }

  const emptyContent = emptyState ?? <EmptyTable />;

  return (
    <tbody>
      {isEmpty ? emptyContent : rows.map((row) => <DataTableRow key={row.id} row={row} />)}
    </tbody>
  );
}

export default DataTableBody;
